import { isPermissionDenied } from "@/hooks/useGuardedMutation"
import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import { useNavigate } from "react-router"
import { toastApiError } from "../../api/apiError"
import { useUploadQuestionnaire } from "../../api/customerRequestsApi"
import { customerRequestConfirmPath } from "./routes"

// Mirrors the backend's list for this destination (`services/upload_formats.py`,
// served by GET /upload-formats). `.xls` isn't offered: the API 415s it.
// DROP_EXTENSIONS is derived from this, not a second hand-maintained list: a
// browse-vs-drag-and-drop mismatch is exactly the drift the shared list exists
// to remove.
export const CUSTOMER_REQUEST_ACCEPTED_TYPES = ".pdf,.xlsx,.csv"
export const CUSTOMER_REQUEST_DROP_EXTENSIONS =
  CUSTOMER_REQUEST_ACCEPTED_TYPES.split(",")

/**
 * Upload one customer request and land on its confirm step. Shared by the
 * import page (button + drop) and the requests overview (drop only), so both
 * entry points run the same upload, duplicate hand-off and error toast.
 */
export function useCustomerRequestUpload() {
  const { translate } = useTranslation()
  const navigate = useNavigate()
  const { mutateAsync: uploadQuestionnaire, isPending } =
    useUploadQuestionnaire()

  const uploadFile = async (file: File) => {
    if (!file || isPending) return
    try {
      const detail = await uploadQuestionnaire(file)
      // The upload still succeeds (the same questionnaire can legitimately arrive
      // twice), but it no longer does so in silence. Six byte-identical PDFs went
      // in unremarked over 45 minutes before this.
      //
      // Carried to the confirm page rather than toasted here: this screen
      // navigates away in the same tick, so a toast fires against a view the
      // user never reads. The duplicate fields are response-only (nothing is
      // stored on the row), so the confirm page cannot refetch them; they
      // travel as route state.
      navigate(customerRequestConfirmPath(detail.id), {
        state: detail.duplicateOfRequestId
          ? {
              duplicateOf: {
                reference: detail.duplicateOfRequestId,
                id: detail.duplicateOfId ?? null,
                uploadedAt: detail.duplicateOfUploadedAt ?? null,
              },
            }
          : undefined,
      })
    } catch (err) {
      // Guard already toasted the permission message; skip our fallback.
      if (isPermissionDenied(err)) return
      toastApiError(err, translate, "MC_Q_UPLOAD_FAILED")
    }
  }

  return { uploadFile, isPending }
}
